import { useEffect, useState } from "react";
import type { TimelinePayload } from "@vzlive/shared";
import { apiFetch } from "../lib/api";
import { resolveCurrentEvent } from "../lib/embed";
import { ReaderPage } from "./ReaderPage";

interface TenantEvents {
  tenant: { locale: "de" | "en" };
  events: TimelinePayload["event"][];
}

/**
 * `/embed/current` — renders whichever event is currently live
 * (or next scheduled / most recent archived) without the host page ever
 * having to repoint the iframe. Re-resolved periodically and when the tab
 * becomes visible again.
 */
export function EmbedCurrentPage() {
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [slug, setSlug] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      try {
        const payload = await apiFetch<TenantEvents>("/api/public/tenant");
        const current = resolveCurrentEvent(payload.events);
        if (!cancelled) {
          if (current)
            setSlug((prev) => (prev === current.slug ? prev : current.slug));
          setState("ready");
        }
      } catch {
        if (!cancelled) setState((s) => (s === "ready" ? s : "error"));
      }
    };
    void resolve();
    const timer = window.setInterval(() => void resolve(), 120_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void resolve();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === "loading") return null;
  if (!slug)
    return (
      <main className="reader-error" id="main">
        <h1>Aktuell kein Event geplant.</h1>
        <p>Hier erscheint automatisch das nächste Live-Event.</p>
      </main>
    );
  return <ReaderPage embed slugOverride={slug} />;
}
